// ---------------------------------------------------------------------------
// simMarket.ts — adapts a fetched ConvictionMarket into the SimMarket the pure
// simulation library consumes. Self-contained: it re-derives the dominant-side
// entry/mark prices the same way the screener's computeRowMetrics does, so the
// simulation page needs no dependency on Screener.tsx.
// ---------------------------------------------------------------------------

import type { ConvictionMarket, DominantSide } from '../api/useConvictionScreener';
import type { SimMarket } from './simulation';

/** Orient a YES-axis probability to the dominant outcome (NO → 1 − p). */
export function outcomeProbability(
    p: number | null,
    dominantSide: DominantSide | null
): number | null {
    if (p == null) return null;
    return dominantSide === 'NO' ? 1 - p : p;
}

/**
 * Which price to treat as the position's "entry" (a) in the PnL math. All
 * options are oriented to the dominant outcome so they're directly comparable
 * to the mark price.
 *   cw_avg_entry — capital-weighted avg price the dominant side paid (real entry)
 *   eq_avg_entry — equal-weighted avg entry (each trader counts the same)
 *   implied      — the group's implied probability (raw_implied), oriented
 */
export type ReferencePrice = 'cw_avg_entry' | 'eq_avg_entry' | 'implied';

export const REFERENCE_PRICE_LABELS: Record<ReferencePrice, string> = {
    cw_avg_entry: 'Capital-wtd avg entry',
    eq_avg_entry: 'Equal-wtd avg entry',
    implied: 'Implied probability',
};

/**
 * Avg entry price the dominant side paid, capital- or equal-weighted. The
 * cw_/eq_avg_entry_price_{yes,no} fields are already oriented to the side that
 * bought, so no inversion is needed.
 */
function entryPriceForSide(market: ConvictionMarket, kind: 'cw' | 'eq'): number | null {
    const ga = market.breakdown?.group_aggregation as Record<string, unknown> | undefined;
    if (!ga) return null;
    const prefix = kind === 'cw' ? 'cw_avg_entry_price' : 'eq_avg_entry_price';
    const key = market.dominant_side === 'NO' ? `${prefix}_no` : `${prefix}_yes`;
    const raw = ga[key];
    return typeof raw === 'number' && !Number.isNaN(raw) ? raw : null;
}

/**
 * Capital-weighted avg entry price of the dominant side — the "Avg Entry"
 * column. Kept for callers that want the cw entry specifically.
 */
export function avgEntryForSide(market: ConvictionMarket): number | null {
    return entryPriceForSide(market, 'cw');
}

/** Resolve the chosen reference price for a market, oriented to the dominant outcome. */
export function referencePrice(market: ConvictionMarket, ref: ReferencePrice): number | null {
    switch (ref) {
        case 'cw_avg_entry':
            return entryPriceForSide(market, 'cw');
        case 'eq_avg_entry':
            return entryPriceForSide(market, 'eq');
        case 'implied':
            return outcomeProbability(market.raw_implied, market.dominant_side);
    }
}

/** max(capital_usd_yes, capital_usd_no) — capital committed on the dominant side. */
export function dominantCapital(market: ConvictionMarket): number {
    return market.dominant_side === 'NO' ? market.capital_usd_no : market.capital_usd_yes;
}

/** Whole days the market has been live (now − startDate), or null if unknown. */
export function daysRunning(startDate: string | null): number | null {
    if (!startDate) return null;
    const t = new Date(startDate).getTime();
    if (Number.isNaN(t)) return null;
    return (Date.now() - t) / 86_400_000;
}

/** Map a fetched market to the minimal shape the simulation needs. */
export function toSimMarket(
    market: ConvictionMarket,
    ref: ReferencePrice = 'cw_avg_entry'
): SimMarket {
    return {
        marketId: market.market_id,
        eventId: market.event_id,
        dominantSide: market.dominant_side,
        avgEntry: referencePrice(market, ref),
        markPrice: outcomeProbability(market.current_yes_prob, market.dominant_side),
        dominantCapital: dominantCapital(market),
        volume: market.volume,
    };
}